import React, { useState } from 'react';
import {
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import {
  AbsoluteFill,
  ListEmptyState,
  ScrollPane,
  SearchInput,
  SimpleTabView,
  Tab,
} from '../../core-ui';
import TagItem from '../VendorPlacement/TagItem';
import { getRemainingBalance } from '../../support/shared/calculatePrice';

import { PlacementByVendor } from '../../types/shared/Placement';
import { Market } from '../../types/shared/Market';
import { Tag } from '../../types/shared/Tag';
import { VendorSummaryWithMarkets as Vendor } from '../../types/shared/Vendor';
import { splitCellID } from './helpers/splitCellID';
import { TbAlertTriangle, TbCircleCheck } from 'react-icons/tb';
import formatCurrency from '../../core/support/formatCurrency';
import { AppStatus } from '../../core/constants/AppStatus';

type DisplayStatus = 'active' | 'waitlisted';

type CellData = {
  index: number;
  date: string;
  vendor: Vendor;
};

type Placement = Map<string, CellData>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedCells: Array<string>;
  onVendorSelect: (vendor: Vendor) => void;
  placement: Placement;
  tags: Array<Tag>;
  market: Market;
  placementByVendor: PlacementByVendor;
  vendors: Array<Vendor>;
};

function AssignVendorModalContent(props: Props) {
  const {
    selectedCells,
    onVendorSelect,
    placement,
    tags,
    market,
    vendors,
  } = props;
  const [searchText, setSearchText] = useState('');
  // Don't show the waitlist tab when doing bulk placement
  const showWaitlist = selectedCells.length === 1;
  const currentVendorId =
    selectedCells.length === 1
      ? placement.get(selectedCells[0] ?? '')?.vendor.id
      : undefined;

  const selectedDates: Array<string> = [];
  for (const cellId of selectedCells) {
    const [dateIndex] = splitCellID(cellId);
    const date = market.dates[dateIndex];
    if (date) {
      selectedDates.push(date);
    }
  }

  const getVendorList = (displayStatus: DisplayStatus) => {
    const isSearchMatch = (vendorName: string) =>
      vendorName.toLowerCase().includes(searchTextNormalized);

    const showVendorStatuses = new Set<string>([AppStatus.ACTIVE]);
    const dateStatus = displayStatus === 'active' ? 'APPROVED' : 'WAITLISTED';

    const searchTextNormalized = searchText.trim().toLowerCase();
    const filteredVendors: Array<Vendor> = [];
    for (const vendor of vendors) {
      if (!isSearchMatch(vendor.name || '')) {
        continue;
      }
      if (!showVendorStatuses.has(vendor.status)) {
        continue;
      }
      const dateSelectionByMarket = vendor.dateSelectionByMarket ?? {};
      const vendorDateSelection = dateSelectionByMarket[market.id] ?? {};
      const hasAnyDates = selectedDates.find(
        (date) => vendorDateSelection[date] === dateStatus,
      );
      if (hasAnyDates) {
        filteredVendors.push(vendor);
      }
    }
    return filteredVendors;
  };

  /**
   * This will return a list of vendorIds that have been placed for any dates of
   * the selected cells.
   */
  const getPlacedVendorsForSelectedDates = () => {
    const vendorIds = new Set<string>();
    for (const cellId of selectedCells) {
      const [dateIndex] = splitCellID(cellId);
      const prefix = `${dateIndex.toString()}:`;
      for (const [key, cellData] of placement) {
        if (key.startsWith(prefix)) {
          vendorIds.add(cellData.vendor.id);
        }
      }
    }
    return vendorIds;
  };

  const renderVendors = (displayStatus: DisplayStatus) => {
    const filteredVendors = getVendorList(displayStatus);
    const placedVendors = getPlacedVendorsForSelectedDates();

    return (
      <VStack align="stretch" px={6} pt={4} pb={6} gap={2}>
        {filteredVendors.length === 0 ? (
          <HStack justify="center">
            <ListEmptyState />
          </HStack>
        ) : null}
        {filteredVendors.map((vendor) => {
          const isActive = currentVendorId === vendor.id;
          const vendorTags = new Set(vendor.tags);
          const tagList = [];
          for (const tag of tags) {
            if (vendorTags.has(tag.id)) {
              tagList.push(<TagItem key={tag.id} tag={tag} />);
            }
          }
          const isPlaced = placedVendors.has(vendor.id);
          const balance = vendor.billing
            ? getRemainingBalance(vendor.billing)
            : 0;
          const formattedBalance = formatCurrency(balance * 100);
          const roundedBalance = Math.round(balance * 100);

          return (
            <HStack
              key={vendor.id}
              borderWidth={2}
              borderRadius={4}
              borderColor={isActive ? 'blue.300' : 'gray.200'}
              backgroundColor={isActive ? 'blue.50' : 'white'}
              paddingX={4}
              paddingY={4}
              cursor="pointer"
              onClick={() => onVendorSelect(vendor)}
            >
              <Text
                color={isPlaced ? 'gray.300' : undefined}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {vendor.name}
              </Text>
              <Spacer />
              <HStack>{tagList}</HStack>
              <Tooltip
                label={t('Balance owed: {balance}', {
                  balance: formattedBalance,
                })}
                placement="top"
              >
                <HStack>
                  {roundedBalance > 0 ? (
                    <Icon as={TbAlertTriangle} fontSize={20} color="red.500" />
                  ) : (
                    <Icon as={TbCircleCheck} fontSize={20} color="green.500" />
                  )}
                </HStack>
              </Tooltip>
            </HStack>
          );
        })}
      </VStack>
    );
  };

  return (
    <VStack flex={1} align="stretch" minH={0} spacing={2}>
      <HStack mx={6} mt={1}>
        <SearchInput
          autoFocus={true}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onClear={() => setSearchText('')}
          containerProps={{ flex: 1 }}
        />
      </HStack>
      <SimpleTabView tabButtonContainerProps={{ px: 6 }}>
        <Tab title={t('Active')} px={0}>
          <AbsoluteFill>
            <ScrollPane>{renderVendors('active')}</ScrollPane>
          </AbsoluteFill>
        </Tab>
        {showWaitlist ? (
          <Tab title={t('Waitlisted')} px={0}>
            <AbsoluteFill>
              <ScrollPane>{renderVendors('waitlisted')}</ScrollPane>
            </AbsoluteFill>
          </Tab>
        ) : null}
      </SimpleTabView>
    </VStack>
  );
}

export function AssignVendorModal(props: Props) {
  const { isOpen, onClose } = props;
  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={() => onClose()}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent height="100%" minHeight="400px">
        <ModalHeader>
          <Text>{t('Choose Vendor')}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          p={0}
          display="flex"
          flexDirection="column"
          align="stretch"
          overflow="hidden"
        >
          <AssignVendorModalContent {...props} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
